import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button, H6, Icon } from "@sellar-io/components";
import { ReactElement } from "react";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { usePaymentLinkCreate } from "../../../hooks/api/v2/paymentLink/usePaymentLinkCreate";

export const SendPaymentLinkModal = ({
  onDismiss,
  active,
  orderId,
  retailerInvoiceEmails,
  hasBankDetails,
  invoicesEnabled,
  invoiceCreationStage: _invoiceCreationStage,
}: {
  onDismiss: () => void;
  active: boolean;
  orderId: number;
  retailerInvoiceEmails: string[];
  hasBankDetails: boolean;
  invoicesEnabled: boolean;
  invoiceCreationStage: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
}): ReactElement => {
  const paymentLinkCreateMutation = usePaymentLinkCreate();

  const handleSendPaymentRequest = () => {
    paymentLinkCreateMutation.mutate(
      { OrderId: orderId },
      {
        onSuccess: () => {
          onDismiss();
        },
      },
    );
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        leftIcon={"CHEVRON_DOWN"}
        title="Send payment request"
      />
      <DialogContent className={"px-4 pb-4"}>
        {paymentLinkCreateMutation.isError && (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(paymentLinkCreateMutation.error)?.response?.data
                .message ?? "An unknown error occurred"
            }
          />
        )}

        {paymentLinkCreateMutation.isPending ? (
          <SpinnerLegacy />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="text-center">
              <H6 className="text-zinc-700">
                Your request for payment will be sent to{" "}
                <span className="font-semibold">
                  {retailerInvoiceEmails.join(", ")}
                </span>
                . Your customer can also access the card payment link when
                viewing this order.
              </H6>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-zinc-800">
                Included in your payment request:
              </p>
              <div className="flex items-center gap-4 rounded-lg bg-zinc-100 p-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-200">
                  <Icon icon={"LINK"} size={1} />
                </div>
                <div className="flex flex-col">
                  <p className="text-base font-semibold text-zinc-800">
                    Card payment link
                  </p>
                  <p className="text-sm text-zinc-700">
                    A link for your customer to pay by card online
                  </p>
                </div>
              </div>

              {hasBankDetails && (
                <div className="flex items-center gap-4 rounded-lg bg-zinc-100 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-200">
                    <Icon icon={"BANK"} size={1} />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-base font-semibold text-zinc-800">
                      Your payment details
                    </p>
                    <p className="text-sm text-zinc-700">
                      For your customers who prefer to pay via bank transfer
                    </p>
                  </div>
                </div>
              )}
            </div>

            {invoicesEnabled && (
              <H6 className="text-sm text-zinc-600">
                Please note: This is a reminder for payment only. You will be
                responsible for issuing the VAT invoice.
              </H6>
            )}
          </div>
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          loading={paymentLinkCreateMutation.isPending}
          onClick={handleSendPaymentRequest}
        >
          Send payment request
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
